import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  writeBatch,
  increment,
  documentId
} from 'firebase/firestore'
import { db, handleFirestoreError, clearFirestoreCache, forceRestartFirestore } from '../config/firebase'
import {
  FlashcardDeck,
  Flashcard,
  StudySession,
  CardProgress,
  FlashcardStats,
  StudyMode,
  FlashcardFilter,
  FlashcardSearchResult,
  CreateFlashcardRequest,
  CreateDeckRequest,
  UpdateCardProgressRequest,
  StudySessionResult,
  SpacedRepetitionResult
} from '../types/flashcard'
import type { User } from 'firebase/auth'
import { getCurrentUser, authService } from './firebase'

// Collections
const DECKS_COLLECTION = 'flashcard_decks'
const CARDS_COLLECTION = 'flashcards'
const SESSIONS_COLLECTION = 'study_sessions'
const PROGRESS_COLLECTION = 'card_progress'
const STATS_COLLECTION = 'flashcard_stats'
const REVIEW_CARDS_COLLECTION = 'review_cards'
const INCORRECT_CARDS_COLLECTION = 'incorrect_cards'

// Utility functions
const convertTimestamp = (timestamp: any): string => {
  if (timestamp?.toDate) {
    return timestamp.toDate().toISOString()
  }
  return new Date(timestamp).toISOString()
}

// Helper: wait for Firebase auth user to be available (useful on hard reload)
const waitForFirebaseUser = async (timeoutMs: number = 2000): Promise<User | null> => {
  const existing = getCurrentUser()
  if (existing) return existing

  return new Promise((resolve) => {
    let unsubscribe: (() => void) | null = null
    const timer = setTimeout(() => {
      if (unsubscribe) unsubscribe()
      resolve(null)
    }, timeoutMs)

    unsubscribe = authService.onAuthStateChanged((user) => {
      if (user) {
        clearTimeout(timer)
        if (unsubscribe) unsubscribe()
        resolve(user)
      }
    })
  })
}

// Spaced Repetition Algorithm (SM-2)
export const calculateSpacedRepetition = (
  quality: number, // 0-5 (0: complete blackout, 5: perfect response)
  previousInterval: number = 1,
  previousEaseFactor: number = 2.5,
  previousDifficulty: number = 0
): SpacedRepetitionResult => {
  let easeFactor = previousEaseFactor
  let interval = previousInterval
  let difficulty = previousDifficulty

  // Update ease factor
  easeFactor = easeFactor + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02))
  if (easeFactor < 1.3) easeFactor = 1.3

  // Update difficulty
  difficulty = difficulty + (8 - 9 * quality) / 17
  if (difficulty < 0) difficulty = 0
  if (difficulty > 1) difficulty = 1

  // Calculate next interval
  if (quality < 3) {
    // If quality is poor, reset interval
    interval = 1
  } else {
    if (interval === 1) {
      interval = 6
    } else {
      interval = Math.round(interval * easeFactor)
    }
  }

  const nextReview = new Date()
  nextReview.setDate(nextReview.getDate() + interval)

  return {
    nextReview: nextReview.toISOString(),
    interval,
    easeFactor,
    difficulty
  }
}

// Deck operations
export const getDecks = async (filter?: FlashcardFilter): Promise<FlashcardDeck[]> => {
  try {
    let user = getCurrentUser()
    if (!user) {
      // On initial page load, Firebase may not have restored auth yet; wait briefly
      user = await waitForFirebaseUser(2000)
    }
    if (!user) {
      // Return empty array if user is not authenticated after waiting
      return []
    }

      const allDecks: FlashcardDeck[] = []
      const processedDeckIds = new Set<string>()
      
      // First, get user's own decks
      let userQuery = query(
        collection(db, DECKS_COLLECTION),
        where('createdBy', '==', user.uid)
      )
      
      // Apply filters to user query
      if (filter?.subject) {
        userQuery = query(userQuery, where('subject', '==', filter.subject))
      }
      if (filter?.topic) {
        userQuery = query(userQuery, where('topic', '==', filter.topic))
      }
      if (filter?.isPublic !== undefined) {
        userQuery = query(userQuery, where('isPublic', '==', filter.isPublic))
      }
      
      const userSnapshot = await getDocs(userQuery)
      
      // Process user's decks
      for (const docSnap of userSnapshot.docs) {
        const data = docSnap.data()
        processedDeckIds.add(docSnap.id)
        
        // Get cards for this deck with retry logic
        try {
          const cardsQuery = query(
            collection(db, CARDS_COLLECTION),
            where('deckId', '==', docSnap.id)
          )
          const cardsSnapshot = await getDocs(cardsQuery)
          const cards = cardsSnapshot.docs.map(cardDoc => ({
            id: cardDoc.id,
            ...cardDoc.data(),
            createdAt: convertTimestamp(cardDoc.data().createdAt),
            updatedAt: convertTimestamp(cardDoc.data().updatedAt)
          } as Flashcard))
          
          allDecks.push({
            id: docSnap.id,
            ...data,
            cards,
            createdAt: convertTimestamp(data.createdAt),
            updatedAt: convertTimestamp(data.updatedAt)
          } as FlashcardDeck)
        } catch (cardError) {
          console.warn(`Error fetching cards for deck ${docSnap.id}:`, cardError)
          // Add deck without cards if card fetching fails
          allDecks.push({
            id: docSnap.id,
            ...data,
            cards: [],
            createdAt: convertTimestamp(data.createdAt),
            updatedAt: convertTimestamp(data.updatedAt)
          } as FlashcardDeck)
        }
      }
      
      // Then, get public decks (excluding user's own decks)
      let publicQuery = query(
        collection(db, DECKS_COLLECTION),
        where('isPublic', '==', true)
      )
      
      // Apply filters to public query
      if (filter?.subject) {
        publicQuery = query(publicQuery, where('subject', '==', filter.subject))
      }
      if (filter?.topic) {
        publicQuery = query(publicQuery, where('topic', '==', filter.topic))
      }
      
      const publicSnapshot = await getDocs(publicQuery)
      
      // Process public decks (excluding user's own decks)
      for (const docSnap of publicSnapshot.docs) {
        // Skip if this deck is already processed (user's own deck)
        if (processedDeckIds.has(docSnap.id)) {
          continue
        }
        
        const data = docSnap.data()
        
        // Get cards for this deck with retry logic
        try {
          const cardsQuery = query(
            collection(db, CARDS_COLLECTION),
            where('deckId', '==', docSnap.id)
          )
          const cardsSnapshot = await getDocs(cardsQuery)
          const cards = cardsSnapshot.docs.map(cardDoc => ({
            id: cardDoc.id,
            ...cardDoc.data(),
            createdAt: convertTimestamp(cardDoc.data().createdAt),
            updatedAt: convertTimestamp(cardDoc.data().updatedAt)
          } as Flashcard))
          
          allDecks.push({
            id: docSnap.id,
            ...data,
            cards,
            createdAt: convertTimestamp(data.createdAt),
            updatedAt: convertTimestamp(data.updatedAt)
          } as FlashcardDeck)
        } catch (cardError) {
          console.warn(`Error fetching cards for deck ${docSnap.id}:`, cardError)
          // Add deck without cards if card fetching fails
          allDecks.push({
            id: docSnap.id,
            ...data,
            cards: [],
            createdAt: convertTimestamp(data.createdAt),
            updatedAt: convertTimestamp(data.updatedAt)
          } as FlashcardDeck)
        }
      }
      
      // Sort decks by updatedAt in descending order (client-side)
      return allDecks.sort((a, b) => {
        const dateA = new Date(a.updatedAt)
        const dateB = new Date(b.updatedAt)
        return dateB.getTime() - dateA.getTime()
      })
    } catch (error) {
      console.error('Error fetching decks:', error)
      return []
    }
}

export const getDeckById = async (deckId: string): Promise<FlashcardDeck> => {
  const maxRetries = 3
  let retryCount = 0
  
  while (retryCount < maxRetries) {
    try {
      const deckDoc = await getDoc(doc(db, DECKS_COLLECTION, deckId))
      if (!deckDoc.exists()) {
        throw new Error('Deste bulunamadı')
      }

      const deckData = deckDoc.data()
      
      // Get cards for this deck with retry logic
      try {
        const cardsQuery = query(
          collection(db, CARDS_COLLECTION),
          where('deckId', '==', deckId)
        )
        const cardsSnapshot = await getDocs(cardsQuery)
        const cards = cardsSnapshot.docs.map(cardDoc => ({
          id: cardDoc.id,
          ...cardDoc.data(),
          createdAt: convertTimestamp(cardDoc.data().createdAt),
          updatedAt: convertTimestamp(cardDoc.data().updatedAt)
        })) as Flashcard[]

        // Sort cards by createdAt in ascending order (client-side)
        cards.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())

        return {
          id: deckDoc.id,
          ...deckData,
          cards,
          totalCards: cards.length,
          createdAt: convertTimestamp(deckData.createdAt),
          updatedAt: convertTimestamp(deckData.updatedAt)
        } as FlashcardDeck
      } catch (cardError) {
        console.warn(`Error fetching cards for deck ${deckId}:`, cardError)
        // Return deck with empty cards array
        return {
          id: deckDoc.id,
          ...deckData,
          cards: [],
          totalCards: 0,
          createdAt: convertTimestamp(deckData.createdAt),
          updatedAt: convertTimestamp(deckData.updatedAt)
        } as FlashcardDeck
      }
    } catch (error) {
       retryCount++
       console.error(`Error fetching deck (attempt ${retryCount}/${maxRetries}):`, error)
       
       if (retryCount >= maxRetries) {
         // Handle critical Firestore errors
         if (error instanceof Error && (
           error.message.includes('INTERNAL ASSERTION FAILED') ||
           error.message.includes('client has already been terminated') ||
           error.message.includes('Unexpected state')
         )) {
           console.warn('🚨 Critical Firestore error in getDeckById - forcing reset...', error.message)
           try {
             await clearFirestoreCache()
             await forceRestartFirestore()
             await new Promise(resolve => setTimeout(resolve, 2000))
             
             // Try one more time with simplified query
             console.log('🔄 Attempting simplified deck query after reset...')
             const retryDeckDoc = await getDoc(doc(db, DECKS_COLLECTION, deckId))
             if (retryDeckDoc.exists()) {
               const retryDeckData = retryDeckDoc.data()
               return {
                 id: retryDeckDoc.id,
                 ...retryDeckData,
                 cards: [], // Return without cards to avoid further errors
                 totalCards: 0,
                 createdAt: convertTimestamp(retryDeckData.createdAt),
                 updatedAt: convertTimestamp(retryDeckData.updatedAt)
               } as FlashcardDeck
             }
           } catch (restartError) {
             console.error('❌ Failed to restart Firestore in getDeckById:', restartError)
           }
         }
         
         await handleFirestoreError(error, 'getDeckById')
         throw new Error(`Deste yüklenirken hata oluştu: ${error}`)
       }
       
       // Wait before retrying (exponential backoff)
       await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000))
     }
  }
  
  throw new Error('Deste yüklenirken hata oluştu')
}

export const createDeck = async (deckData: CreateDeckRequest): Promise<FlashcardDeck> => {
  try {
    const user = getCurrentUser()
    if (!user) throw new Error('Kullanıcı oturumu bulunamadı')
    const userId = user.uid
    const now = Timestamp.now()
    
    const newDeck = {
      ...deckData,
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
      totalCards: 0
    }

    const docRef = await addDoc(collection(db, DECKS_COLLECTION), newDeck)
    
    return {
      id: docRef.id,
      ...newDeck,
      cards: [],
      createdAt: now.toDate(),
      updatedAt: now.toDate()
    } as FlashcardDeck
  } catch (error) {
    console.error('Error creating deck:', error)
    throw new Error('Deste oluşturulurken hata oluştu')
  }
}

export const createDeckWithCards = async (
  deckData: CreateDeckRequest, 
  cardsData: Omit<CreateFlashcardRequest, 'deckId'>[]
): Promise<FlashcardDeck> => {
  try {
    const user = getCurrentUser()
    if (!user) throw new Error('Kullanıcı oturumu bulunamadı')
    const userId = user.uid
    const now = Timestamp.now()
    const batch = writeBatch(db)
    
    // Create deck
    const deckRef = doc(collection(db, DECKS_COLLECTION))
    const newDeck = {
      ...deckData,
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
      totalCards: cardsData.length
    }
    batch.set(deckRef, newDeck)
    
    // Create cards
    const cards: Flashcard[] = []
    for (const cardData of cardsData) {
      const cardRef = doc(collection(db, CARDS_COLLECTION))
      const newCard = {
        ...cardData,
        deckId: deckRef.id,
        createdBy: userId,
        createdAt: now,
        updatedAt: now
      }
      batch.set(cardRef, newCard)
      
      cards.push({
        id: cardRef.id,
        ...newCard,
        createdAt: now.toDate(),
        updatedAt: now.toDate()
      } as Flashcard)
    }
    
    await batch.commit()
    
    return {
      id: deckRef.id,
      ...newDeck,
      cards,
      createdAt: now.toDate(),
      updatedAt: now.toDate()
    } as FlashcardDeck
  } catch (error) {
    console.error('Error creating deck with cards:', error)
    throw new Error('Deste ve kartlar oluşturulurken hata oluştu')
  }
}

export const updateDeck = async (deckId: string, updates: Partial<FlashcardDeck>): Promise<FlashcardDeck> => {
  try {
    const deckRef = doc(db, DECKS_COLLECTION, deckId)
    const updateData = {
      ...updates,
      updatedAt: Timestamp.now()
    }
    
    await updateDoc(deckRef, updateData)
    return await getDeckById(deckId)
  } catch (error) {
    console.error('Error updating deck:', error)
    throw new Error('Deste güncellenirken hata oluştu')
  }
}

export const deleteDeck = async (deckId: string): Promise<void> => {
  try {
    const batch = writeBatch(db)
    
    // Delete all cards in the deck
    const cardsQuery = query(
      collection(db, CARDS_COLLECTION),
      where('deckId', '==', deckId)
    )
    const cardsSnapshot = await getDocs(cardsQuery)
    cardsSnapshot.docs.forEach(cardDoc => {
      batch.delete(cardDoc.ref)
    })
    
    // Delete the deck
    batch.delete(doc(db, DECKS_COLLECTION, deckId))
    
    await batch.commit()
  } catch (error) {
    console.error('Error deleting deck:', error)
    throw new Error('Deste silinirken hata oluştu')
  }
}

// Card operations
export const createCard = async (cardData: CreateFlashcardRequest): Promise<Flashcard> => {
  try {
    const user = getCurrentUser()
    if (!user) throw new Error('Kullanıcı oturumu bulunamadı')
    const userId = user.uid
    const now = Timestamp.now()
    
    const newCard = {
      ...cardData,
      createdBy: userId,
      createdAt: now,
      updatedAt: now
    }

    const docRef = await addDoc(collection(db, CARDS_COLLECTION), newCard)
    
    // Update deck's total cards count
    const deckRef = doc(db, DECKS_COLLECTION, cardData.deckId)
    const deckDoc = await getDoc(deckRef)
    if (deckDoc.exists()) {
      await updateDoc(deckRef, {
        totalCards: (deckDoc.data().totalCards || 0) + 1,
        updatedAt: now
      })
    }
    
    return {
      id: docRef.id,
      ...newCard,
      createdAt: now.toDate(),
      updatedAt: now.toDate()
    } as Flashcard
  } catch (error) {
    console.error('Error creating card:', error)
    throw new Error('Kart oluşturulurken hata oluştu')
  }
}

export const updateCard = async (cardId: string, updates: Partial<Flashcard>): Promise<Flashcard> => {
  try {
    const cardRef = doc(db, CARDS_COLLECTION, cardId)
    const updateData = {
      ...updates,
      updatedAt: Timestamp.now()
    }
    
    await updateDoc(cardRef, updateData)
    
    const updatedDoc = await getDoc(cardRef)
    const data = updatedDoc.data()!
    
    return {
      id: cardId,
      ...data,
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt)
    } as Flashcard
  } catch (error) {
    console.error('Error updating card:', error)
    throw new Error('Kart güncellenirken hata oluştu')
  }
}

export const deleteCard = async (cardId: string): Promise<void> => {
  try {
    // Get card to find its deck
    const cardDoc = await getDoc(doc(db, CARDS_COLLECTION, cardId))
    if (!cardDoc.exists()) {
      // If the card does not exist, treat as successful (idempotent delete)
      return
    }
    
    const cardData = cardDoc.data()
    const deckId = (cardData as any).deckId
    
    // Delete the card
    await deleteDoc(doc(db, CARDS_COLLECTION, cardId))
    
    // Update deck's total cards count
    const deckRef = doc(db, DECKS_COLLECTION, deckId)
    const deckDoc = await getDoc(deckRef)
    if (deckDoc.exists()) {
      await updateDoc(deckRef, {
        totalCards: Math.max((deckDoc.data().totalCards || 1) - 1, 0),
        updatedAt: Timestamp.now()
      })
    }
  } catch (error) {
    console.error('Error deleting card:', error)
    throw new Error('Kart silinirken hata oluştu')
  }
}

// Study session operations
export const startStudySession = async (deckId: string, mode: StudyMode): Promise<StudySession> => {
  try {
    const user = getCurrentUser()
    if (!user) throw new Error('Kullanıcı oturumu bulunamadı')
    const userId = user.uid
    const nowTs = Timestamp.now()
    const now = new Date()
    
    const session: Omit<StudySession, 'id'> = {
      deckId,
      userId,
      startTime: now.toISOString(),
      mode: mode.type,
      cardsStudied: 0,
      cardsCorrect: 0,
      cardsIncorrect: 0,
      cardsToReview: 0,
      duration: 0,
      isCompleted: false
    }

    const docRef = await addDoc(collection(db, SESSIONS_COLLECTION), {
      ...session,
      // Persist as Firestore Timestamp
      startTime: nowTs
    })
    
    return {
      id: docRef.id,
      ...session
    }
  } catch (error) {
    console.error('Error starting study session:', error)
    throw new Error('Çalışma oturumu başlatılırken hata oluştu')
  }
}

export const updateCardProgress = async (progressData: UpdateCardProgressRequest): Promise<CardProgress> => {
  try {
    const user = getCurrentUser()
    if (!user) throw new Error('Kullanıcı oturumu bulunamadı')
    const userId = user.uid
    const now = new Date()
    
    // Convert status to quality score for spaced repetition
    let quality: number
    switch (progressData.status) {
      case 'correct':
        quality = 5
        break
      case 'incorrect':
        quality = 0
        break
      case 'review':
        quality = 3
        break
      default:
        quality = 3
    }
    
    // Get existing progress or create new
    const progressQuery = query(
      collection(db, PROGRESS_COLLECTION),
      where('cardId', '==', progressData.cardId),
      where('userId', '==', userId)
    )
    const progressSnapshot = await getDocs(progressQuery)
    
    let existingProgress: CardProgress | null = null
    let progressDocId: string | null = null
    
    if (!progressSnapshot.empty) {
      const docSnap = progressSnapshot.docs[0]
      progressDocId = docSnap.id
      const data = docSnap.data()
      existingProgress = {
        ...data,
        lastStudied: convertTimestamp(data.lastStudied),
        nextReview: convertTimestamp(data.nextReview)
      } as CardProgress
    }
    
    // Calculate spaced repetition
    const spacedRepetition = calculateSpacedRepetition(
      quality,
      existingProgress?.interval || 1,
      existingProgress?.easeFactor || 2.5,
      existingProgress?.difficulty || 0
    )
    
    // Determine new status
    let newStatus: CardProgress['status']
    if (quality >= 4) {
      newStatus = existingProgress?.status === 'new' ? 'learning' : 
                 existingProgress?.correctCount >= 2 ? 'mastered' : 'review'
    } else if (quality >= 3) {
      newStatus = 'review'
    } else {
      newStatus = 'learning'
    }
    
    const updatedProgress: CardProgress = {
      cardId: progressData.cardId,
      userId,
      status: newStatus,
      correctCount: (existingProgress?.correctCount || 0) + (quality >= 3 ? 1 : 0),
      incorrectCount: (existingProgress?.incorrectCount || 0) + (quality < 3 ? 1 : 0),
      lastStudied: now.toISOString(),
      nextReview: spacedRepetition.nextReview,
      difficulty: spacedRepetition.difficulty,
      interval: spacedRepetition.interval,
      easeFactor: spacedRepetition.easeFactor
    }
    
    if (progressDocId) {
      await updateDoc(doc(db, PROGRESS_COLLECTION, progressDocId), {
        ...updatedProgress,
        // Persist as Firestore Timestamps
        lastStudied: Timestamp.fromDate(now),
        nextReview: Timestamp.fromDate(new Date(spacedRepetition.nextReview))
      })
    } else {
      await addDoc(collection(db, PROGRESS_COLLECTION), {
        ...updatedProgress,
        // Persist as Firestore Timestamps
        lastStudied: Timestamp.fromDate(now),
        nextReview: Timestamp.fromDate(new Date(spacedRepetition.nextReview))
      })
    }

    // NEW: Update current study session counters atomically if sessionId is provided
    if (progressData.sessionId) {
      try {
        const sessionRef = doc(db, SESSIONS_COLLECTION, progressData.sessionId)
        const fields: any = { cardsStudied: increment(1) }
        if (progressData.status === 'correct') fields.cardsCorrect = increment(1)
        if (progressData.status === 'incorrect') fields.cardsIncorrect = increment(1)
        if (progressData.status === 'review') fields.cardsToReview = increment(1)
        await updateDoc(sessionRef, fields)
      } catch (sessionErr) {
        console.warn('Session counters could not be updated:', sessionErr)
      }
    }
    
    return updatedProgress
  } catch (error) {
    console.error('Error updating card progress:', error)
    throw new Error('Kart ilerlemesi güncellenirken hata oluştu')
  }
}

export const completeStudySession = async (sessionId: string): Promise<StudySessionResult> => {
  try {
    const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId)
    const sessionDoc = await getDoc(sessionRef)
    
    if (!sessionDoc.exists()) {
      throw new Error('Çalışma oturumu bulunamadı')
    }
    
    const sessionData = sessionDoc.data() as any
    const endTime = new Date()
    const startTimeMs = new Date(convertTimestamp(sessionData.startTime)).getTime()
    const duration = Math.floor((endTime.getTime() - startTimeMs) / 1000)
    const cardsStudied = (sessionData.cardsStudied as number) || 0
    const cardsCorrect = (sessionData.cardsCorrect as number) || 0
    const cardsIncorrect = (sessionData.cardsIncorrect as number) || 0
    const score = cardsStudied > 0 ? Math.round((cardsCorrect / cardsStudied) * 100) : 0
    
    // Update session
    await updateDoc(sessionRef, {
      endTime: Timestamp.fromDate(endTime),
      duration,
      isCompleted: true
    })
    
    // Calculate XP earned (base: 10 XP per correct answer, bonus for high scores)
    let xpEarned = cardsCorrect * 10
    if (score >= 90) xpEarned += 50 // Bonus for excellent performance
    else if (score >= 80) xpEarned += 25 // Bonus for good performance
    
    // TODO: Check for badges earned and update user stats
    const badgesEarned: string[] = []
    
    return {
      sessionId,
      cardsStudied,
      cardsCorrect,
      cardsIncorrect,
      duration,
      score,
      xpEarned,
      badgesEarned
    }
  } catch (error) {
    console.error('Error completing study session:', error)
    throw new Error('Çalışma oturumu tamamlanırken hata oluştu')
  }
}

// Stats operations
export const getStats = async (): Promise<FlashcardStats> => {
  const maxRetries = 3
  let retryCount = 0
  
  while (retryCount < maxRetries) {
    try {
      const user = getCurrentUser()
      if (!user) {
        // Return default stats if user is not authenticated
        return {
          totalDecks: 0,
          totalCards: 0,
          totalStudySessions: 0,
          totalStudyTime: 0,
          averageScore: 0,
          cardsReviewed: 0,
          cardsLearned: 0,
          currentStreak: 0,
          longestStreak: 0,
          subjectStats: {},
          difficultyStats: {
            easy: 0,
            medium: 0,
            hard: 0
          },
          weeklyProgress: [],
          recentSessions: []
        }
      }
      const userId = user.uid
      
      // Get user's decks count with error handling
      let totalDecks = 0
      let totalCards = 0
      try {
        const decksQuery = query(
          collection(db, DECKS_COLLECTION),
          where('createdBy', '==', userId)
        )
        const decksSnapshot = await getDocs(decksQuery)
        totalDecks = decksSnapshot.size
        
        // Get total cards count
        for (const deckDoc of decksSnapshot.docs) {
          const deckData = deckDoc.data()
          totalCards += deckData.totalCards || 0
        }
      } catch (decksError) {
        console.warn('Error fetching decks for stats:', decksError)
      }
      
      // Get card progress stats with error handling
      let cardsStudied = 0
      let cardsMastered = 0
      let cardsToReview = 0
      const now = new Date()
      
      try {
        const progressQuery = query(
          collection(db, PROGRESS_COLLECTION),
          where('userId', '==', userId)
        )
        const progressSnapshot = await getDocs(progressQuery)
        
        progressSnapshot.docs.forEach(doc => {
          const progress = doc.data() as CardProgress
          cardsStudied++
          
          if (progress.status === 'mastered') {
            cardsMastered++
          }
          
          const nextReview = convertTimestamp(progress.nextReview)
          if (nextReview <= now) {
            cardsToReview++
          }
        })
      } catch (progressError) {
        console.warn('Error fetching progress for stats:', progressError)
      }
      
      // Get study sessions for streak and time calculation with error handling
      let totalStudyTime = 0
      let totalScore = 0
      let studyStreak = 0
      let lastStudyDate: Date | null = null
      const studyDates = new Set<string>()
      let sessionsCount = 0
      
      try {
        const sessionsQuery = query(
          collection(db, SESSIONS_COLLECTION),
          where('userId', '==', userId),
          where('isCompleted', '==', true),
          orderBy('startTime', 'desc')
        )
        const sessionsSnapshot = await getDocs(sessionsQuery)
        sessionsCount = sessionsSnapshot.size
        
        sessionsSnapshot.docs.forEach(doc => {
          const session = doc.data() as StudySession
          totalStudyTime += Math.floor(session.duration / 60) // Convert to minutes
          
          if (session.cardsStudied > 0) {
            totalScore += Math.round((session.cardsCorrect / session.cardsStudied) * 100)
          }
          
          const sessionDate = convertTimestamp(session.startTime)
          const dateStr = sessionDate.toDateString()
          studyDates.add(dateStr)
          
          if (!lastStudyDate || sessionDate > lastStudyDate) {
            lastStudyDate = sessionDate
          }
        })
        
        // Calculate study streak
        if (lastStudyDate) {
          const today = new Date()
          const yesterday = new Date(today)
          yesterday.setDate(yesterday.getDate() - 1)
          
          if (studyDates.has(today.toDateString()) || studyDates.has(yesterday.toDateString())) {
            let checkDate = new Date(today)
            while (studyDates.has(checkDate.toDateString())) {
              studyStreak++
              checkDate.setDate(checkDate.getDate() - 1)
            }
          }
        }
      } catch (sessionsError) {
        console.warn('Error fetching sessions for stats:', sessionsError)
      }
      
      const averageScore = sessionsCount > 0 ? Math.round(totalScore / sessionsCount) : 0
      
      return {
        userId,
        totalDecks,
        totalCards,
        cardsStudied,
        cardsMastered,
        cardsToReview,
        studyStreak,
        totalStudyTime,
        averageScore,
        lastStudyDate: lastStudyDate || new Date(),
        weeklyGoal: 100, // Default weekly goal
        weeklyProgress: Math.min(cardsStudied, 100),
        monthlyStats: [] // TODO: Implement monthly stats calculation
      }
    } catch (error) {
       retryCount++
       console.error(`Error fetching stats (attempt ${retryCount}/${maxRetries}):`, error)
       
       if (retryCount >= maxRetries) {
         await handleFirestoreError(error, 'getStats')
         throw new Error(`İstatistikler yüklenirken hata oluştu: ${error}`)
       }
       
       // Wait before retrying (exponential backoff)
       await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000))
     }
  }
  
  throw new Error('İstatistikler yüklenirken hata oluştu')
}

// Search operations
export const searchFlashcards = async (
  query: string,
  filter?: FlashcardFilter,
  page: number = 1,
  limit: number = 20,
  existingDecks?: FlashcardDeck[]
): Promise<FlashcardSearchResult> => {
  try {
    const user = getCurrentUser()
    if (!user) {
      // Return empty search result if user is not authenticated
      return {
        decks: [],
        cards: [],
        total: 0,
        page: 1,
        totalPages: 0,
        hasMore: false
      }
    }
    
    // Use existing decks if provided, otherwise fetch them
    const decks = existingDecks || await getDecks(filter)
    const allCards: Flashcard[] = []
    
    decks.forEach(deck => {
      allCards.push(...deck.cards)
    })
    
    // Filter by search query
    const searchLower = query.toLowerCase().trim()
    
    if (!searchLower) {
      return {
        decks: [],
        cards: [],
        total: 0,
        page: 1,
        totalPages: 0,
        hasMore: false
      }
    }
    
    const filteredDecks = decks.filter(deck => 
      deck.title.toLowerCase().includes(searchLower) ||
      deck.description?.toLowerCase().includes(searchLower) ||
      deck.subject.toLowerCase().includes(searchLower) ||
      deck.topic.toLowerCase().includes(searchLower)
    )
    
    const filteredCards = allCards.filter(card =>
      card.question.toLowerCase().includes(searchLower) ||
      card.answer.toLowerCase().includes(searchLower) ||
      card.hint?.toLowerCase().includes(searchLower) ||
      card.explanation?.toLowerCase().includes(searchLower) ||
      card.tags.some(tag => tag.toLowerCase().includes(searchLower))
    )
    
    // Pagination
    const startIndex = (page - 1) * limit
    const paginatedDecks = filteredDecks.slice(startIndex, startIndex + limit)
    const paginatedCards = filteredCards.slice(startIndex, startIndex + limit)
    
    const totalResults = filteredDecks.length + filteredCards.length
    const totalPages = Math.ceil(totalResults / limit)
    
    return {
      decks: paginatedDecks,
      cards: paginatedCards,
      total: totalResults,
      page,
      totalPages,
      hasMore: page < totalPages,
      limit
    }
  } catch (error) {
    console.error('Error searching flashcards:', error)
    throw new Error('Arama sırasında hata oluştu')
  }
}

// Save incorrect cards for later review
export const saveIncorrectCard = async (cardId: string, deckId: string, sessionId?: string): Promise<void> => {
  try {
    const user = getCurrentUser()
    if (!user) throw new Error('Kullanıcı oturumu bulunamadı')
    const userId = user.uid
    const now = Timestamp.now()
    
    // Check if card already exists in incorrect cards
    const existingQuery = query(
      collection(db, INCORRECT_CARDS_COLLECTION),
      where('cardId', '==', cardId),
      where('userId', '==', userId),
      where('deckId', '==', deckId)
    )
    const existingSnapshot = await getDocs(existingQuery)
    
    if (existingSnapshot.empty) {
      // Add new incorrect card record
      await addDoc(collection(db, INCORRECT_CARDS_COLLECTION), {
        cardId,
        deckId,
        userId,
        sessionId: sessionId || null,
        addedAt: now,
        reviewCount: 0,
        lastReviewed: null
      })
    } else {
      // Update existing record
      const docRef = existingSnapshot.docs[0].ref
      await updateDoc(docRef, {
        addedAt: now,
        sessionId: sessionId || null
      })
    }
  } catch (error) {
    console.error('Error saving incorrect card:', error)
    throw new Error('Yanlış kart kaydedilirken hata oluştu')
  }
}

// Save cards marked for review
export const saveReviewCard = async (cardId: string, deckId: string, sessionId?: string): Promise<void> => {
  try {
    const user = getCurrentUser()
    if (!user) throw new Error('Kullanıcı oturumu bulunamadı')
    const userId = user.uid
    const now = Timestamp.now()
    
    // Check if card already exists in review cards
    const existingQuery = query(
      collection(db, REVIEW_CARDS_COLLECTION),
      where('cardId', '==', cardId),
      where('userId', '==', userId),
      where('deckId', '==', deckId)
    )
    const existingSnapshot = await getDocs(existingQuery)
    
    if (existingSnapshot.empty) {
      // Add new review card record
      await addDoc(collection(db, REVIEW_CARDS_COLLECTION), {
        cardId,
        deckId,
        userId,
        sessionId: sessionId || null,
        addedAt: now,
        reviewCount: 0,
        lastReviewed: null
      })
    } else {
      // Update existing record
      const docRef = existingSnapshot.docs[0].ref
      await updateDoc(docRef, {
        addedAt: now,
        sessionId: sessionId || null
      })
    }
  } catch (error) {
    console.error('Error saving review card:', error)
    throw new Error('Tekrar kartı kaydedilirken hata oluştu')
  }
}

// Get incorrect cards by deck
export const getIncorrectCardsByDeck = async (deckId: string): Promise<Flashcard[]> => {
  try {
    const user = getCurrentUser()
    if (!user) return []
    const userId = user.uid
    
    // Get incorrect card records for this deck
    const incorrectQuery = query(
      collection(db, INCORRECT_CARDS_COLLECTION),
      where('userId', '==', userId),
      where('deckId', '==', deckId)
    )
    const incorrectSnapshot = await getDocs(incorrectQuery)
    
    const cardIds = incorrectSnapshot.docs.map(doc => doc.data().cardId)
    
    if (cardIds.length === 0) {
      return []
    }
    
    // Get the actual cards
    const cards: Flashcard[] = []
    
    // Firebase doesn't support 'in' queries with more than 10 items
    const batchSize = 10
    for (let i = 0; i < cardIds.length; i += batchSize) {
      const batch = cardIds.slice(i, i + batchSize)
      const cardsQuery = query(
        collection(db, CARDS_COLLECTION),
        where(documentId(), 'in', batch)
      )
      const cardsSnapshot = await getDocs(cardsQuery)
      
      cardsSnapshot.docs.forEach(doc => {
        const data = doc.data()
        cards.push({
          id: doc.id,
          ...data,
          createdAt: convertTimestamp(data.createdAt),
          updatedAt: convertTimestamp(data.updatedAt)
        } as Flashcard)
      })
    }
    
    return cards
  } catch (error) {
    console.error('Error getting incorrect cards by deck:', error)
    throw new Error('Yanlış kartlar yüklenirken hata oluştu')
  }
}

// Get review cards by deck
export const getReviewCardsByDeck = async (deckId: string): Promise<Flashcard[]> => {
  try {
    const user = getCurrentUser()
    if (!user) return []
    const userId = user.uid
    
    // Get review card records for this deck
    const reviewQuery = query(
      collection(db, REVIEW_CARDS_COLLECTION),
      where('userId', '==', userId),
      where('deckId', '==', deckId)
    )
    const reviewSnapshot = await getDocs(reviewQuery)
    
    const cardIds = reviewSnapshot.docs.map(doc => doc.data().cardId)
    
    if (cardIds.length === 0) {
      return []
    }
    
    // Get the actual cards
    const cards: Flashcard[] = []
    
    // Firebase doesn't support 'in' queries with more than 10 items
    const batchSize = 10
    for (let i = 0; i < cardIds.length; i += batchSize) {
      const batch = cardIds.slice(i, i + batchSize)
      const cardsQuery = query(
        collection(db, CARDS_COLLECTION),
        where(documentId(), 'in', batch)
      )
      const cardsSnapshot = await getDocs(cardsQuery)
      
      cardsSnapshot.docs.forEach(doc => {
        const data = doc.data()
        cards.push({
          id: doc.id,
          ...data,
          createdAt: convertTimestamp(data.createdAt),
          updatedAt: convertTimestamp(data.updatedAt)
        } as Flashcard)
      })
    }
    
    return cards
  } catch (error) {
    console.error('Error getting review cards by deck:', error)
    throw new Error('Tekrar kartları yüklenirken hata oluştu')
  }
}

// Get cards that need review
export const getReviewCards = async (): Promise<Flashcard[]> => {
  try {
    const user = getCurrentUser()
    if (!user) {
      // Return empty array if user is not authenticated
      return []
    }
    const userId = user.uid
    const now = new Date()
    
    // Get cards that need review
    const progressQuery = query(
      collection(db, PROGRESS_COLLECTION),
      where('userId', '==', userId),
      where('nextReview', '<=', Timestamp.fromDate(now))
    )
    const progressSnapshot = await getDocs(progressQuery)
    
    const cardIds = progressSnapshot.docs.map(doc => doc.data().cardId)
    
    if (cardIds.length === 0) {
      return []
    }
    
    // Get the actual cards
    const cards: Flashcard[] = []
    
    // Firebase doesn't support 'in' queries with more than 10 items,
    // so we need to batch the requests
    const batchSize = 10
    for (let i = 0; i < cardIds.length; i += batchSize) {
      const batch = cardIds.slice(i, i + batchSize)
      const cardsQuery = query(
        collection(db, CARDS_COLLECTION),
        where(documentId(), 'in', batch)
      )
      const cardsSnapshot = await getDocs(cardsQuery)
      
      cardsSnapshot.docs.forEach(doc => {
        const data = doc.data()
        cards.push({
          id: doc.id,
          ...data,
          createdAt: convertTimestamp(data.createdAt),
          updatedAt: convertTimestamp(data.updatedAt)
        } as Flashcard)
      })
    }
    
    return cards
  } catch (error) {
    console.error('Error getting review cards:', error)
    throw new Error('Tekrar kartları yüklenirken hata oluştu')
  }
}

// Export all functions
export const flashcardService = {
  getDecks,
  getDeckById,
  createDeck,
  createDeckWithCards,
  updateDeck,
  deleteDeck,
  createCard,
  updateCard,
  deleteCard,
  startStudySession,
  updateCardProgress,
  completeStudySession,
  getStats,
  searchFlashcards,
  getReviewCards,
  saveIncorrectCard,
  saveReviewCard,
  getIncorrectCardsByDeck,
  getReviewCardsByDeck
}